import { useState, useCallback, useEffect } from "react";
import styles from "./MoveOutChecklist.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import Header from "./components/Header";
import MoveOutForm from "./components/MoveOutFormTab";
import CheckListTab from "./components/CheckListTab";
import DamageCostTab from "./components/DamageCostTab";
import { Check, X } from "react-feather";
import StatementTab from "./components/StatementTab";
import SubmittedTab from "./components/SubmittedTab";
import { Badge } from "./components/CommonComponents";

const MoveOutReport = ({ setMoveOutChecklistOpen, moveOutChecklistOpen, leaseData, setLoading }) => {
    const [tab, setTab] = useState(0);
    const [flagged, setFlagged] = useState(false);
    const [inspReady, setInspReady] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [toast, setToast] = useState({ msg: "", visible: false });
    const showToast = useCallback((msg) => {
        setToast({ msg, visible: true });
        setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2200);
    }, []);

    const phaseBadge = (() => {
        if (submitted) return { type: "success", label: "Statement sent" };
        if (!flagged) return { type: "blue", label: "Phase 1: Flag Move-Out" };
        if (!inspReady) return { type: "warn", label: "Awaiting inspection" };
        if (tab === 1) return { type: "success", label: "Inspection: In progress" };
        if (tab === 3) return { type: "success", label: "Final statement" };
        return { type: "blue", label: "Inspection: In progress" };
    })();

    const tryTab = (n) => {
        if (n === 1 && !flagged) { showToast("Flag the move-out first."); return; }
        if (n >= 2 && !inspReady) { showToast("Checklist unlocks on inspection day."); return; }
        setTab(n);
    }

    const tabStatus = (i) => {
        if (i === tab && !submitted) return "active";
        if (submitted || i < tab) return "done";
        if (!flagged && i > 0) return "locked";
        if (!inspReady && i >= 2) return "locked";
        return "";
    };

    const { getAccessTokenSilently } = useAuth0();
    const [propertyDetails, setPropertyDetails] = useState(null);
    const [moveoutData, setMoveoutData] = useState(null);

    const tabLabels = ["Flag move-out", "Inspection Checklist", "Damage costs", "Final statement"];

    useEffect(() => {
        const fetchProperty = async () => {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-details/" + leaseData?.property_id, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setPropertyDetails(response.data.data);
            }).catch((error) => { });
        }
        if (leaseData?.property_id) {
            fetchProperty();
        }
    }, [leaseData?.property_id]);

    useEffect(() => {
        const fetchMoveoutData = async () => {
            setLoading(true);
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/moveout-data/" + leaseData?.tenant_id, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setLoading(false);
                let moveoutDateT = response.data.data;

                if (moveoutDateT) {
                    setMoveoutData(response.data.data);
                    setFlagged(true);
                    setInspReady(true);

                    if (moveoutDateT?.is_submitted === 1) {
                        setSubmitted(true);
                    }

                    if (moveoutDateT?.current_step === 3) {
                        setTab(3);
                    }

                    if (moveoutDateT?.current_step === 2) {
                        setTab(2);
                    }

                    if (moveoutDateT?.current_step === 1) {
                        setTab(1);
                    }
                }
            }).catch((error) => {
                setLoading(false);
            });
        }
        if (leaseData?.tenant_id) {
            fetchMoveoutData();
        }
    }, [leaseData?.tenant_id]);

    const submitMoveoutChceckList = async (data) => {
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        await axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/tenants/move-out/" + leaseData?.tenant_id, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then((response) => {
            setLoading(false);

            setMoveoutData(response.data.data);

            let moveoutDateT = response.data.data;

            if (moveoutDateT?.current_step === 1) {
                setFlagged(true);
                setInspReady(true);
                setTab(1);
            }

            if (moveoutDateT?.current_step === 2) {
                setInspReady(true);
                setTab(2);
            }

            if (moveoutDateT?.current_step === 3) {
                setFlagged(true);
                setInspReady(true);
                setTab(3);
            }

            if (moveoutDateT?.is_submitted === 1) {
                setSubmitted(true);
            }
        }).catch((error) => {
            setLoading(false);
        });
    }

    return (
        <div className={styles.root}>

            <div className={styles.mainWrap}>
                <div className={styles.pageBody}>
                    <div className={styles.reportPanel}>
                        <div className={styles.modalCard}>
                            <div className={styles.modalHdr}>
                                <div className={styles.modalTitleRow}>
                                    <div className={styles.modalTitle}>Move-Out Condition Report</div>
                                    <Badge type={phaseBadge.type}>{phaseBadge.label}</Badge>
                                </div>
                                <button className={styles.modalClose} onClick={(e) => setMoveOutChecklistOpen(false)}><X /></button>
                            </div>

                            <div className={styles.modalBody}>
                                <Header leaseData={leaseData} propertyDetails={propertyDetails} />

                                <div className={styles.stepTabs}>
                                    {tabLabels.map((label, i) => {
                                        const s = tabStatus(i);
                                        return (<button key={i} className={`${styles.stepTab} ${s === "active" ? styles.stepTabActive : ""} ${s === "done" ? styles.stepTabDone : ""} ${s === "locked" ? styles.stepTabLocked : ""}`} onClick={() => tryTab(i)}>
                                            <span className={styles.tabNum}>
                                                {s === "done" ? <Check /> : i + 1}
                                            </span>
                                            {label}
                                        </button>
                                        );
                                    })}
                                </div>

                                {/* Tab content */}
                                {tab === 0 && !submitted && (<MoveOutForm moveoutData={moveoutData} submitMoveoutChceckList={submitMoveoutChceckList.bind(this)} leaseData={leaseData} propertyDetails={propertyDetails} />)}
                                {/*tab === 1 && !submitted && (<InspectionWaiting moveoutData={moveoutData} submitMoveoutChceckList={submitMoveoutChceckList.bind(this)} />)*/}
                                {tab === 1 && !submitted && (<CheckListTab moveoutData={moveoutData} submitMoveoutChceckList={submitMoveoutChceckList.bind(this)} setLoading={setLoading.bind(this)} setTab={setTab.bind(this)} propertyDetails={propertyDetails} />)}

                                {tab === 2 && !submitted && (<DamageCostTab moveoutData={moveoutData} submitMoveoutChceckList={submitMoveoutChceckList.bind(this)} setTab={setTab.bind(this)} />)}

                                {tab === 3 && !submitted && (<StatementTab leaseData={leaseData} moveoutData={moveoutData} submitMoveoutChceckList={submitMoveoutChceckList.bind(this)} propertyDetails={propertyDetails} setLoading={setLoading.bind(this)} setTab={setTab.bind(this)} />)}

                                {submitted && <SubmittedTab leaseData={leaseData} moveoutData={moveoutData} />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast */}
            <div className={`${styles.toast} ${toast.visible ? styles.toastVisible : styles.toastHidden}`}>
                {toast.msg}
            </div>
        </div>
    );
}

export default MoveOutReport;